import React, { useEffect, useState } from 'react';
import { Show, useGetList, useNotify, usePermissions, useRecordContext, useRedirect, useRefresh, useTranslate } from 'react-admin';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { errorLine } from '../kiosk-releases/labels';
import type { KioskGroupDetail } from '../kiosk-releases/types';
import { KIOSK_GROUPS_BASE } from './KioskGroupsList';

const textOf = (value: string) => (value.trim() === '' ? null : value.trim());

export const KioskGroupDetails = ({ record }: { record: KioskGroupDetail }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { permissions } = usePermissions();
  const writer = hasPermission(permissions as string[] | null | undefined, 'kiosk-releases:write:all');
  const kiosks = useGetList('kiosk-devices', { pagination: { page: 1, perPage: 100 }, sort: { field: 'name', order: 'ASC' } });
  const [name, setName] = useState(record.name);
  const [description, setDescription] = useState(record.description === null ? '' : record.description);
  const [choosing, setChoosing] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const allGroups = useGetList('kiosk-groups', { pagination: { page: 1, perPage: 100 }, sort: { field: 'name', order: 'ASC' } });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmTest, setConfirmTest] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const g = (key: string, options?: any) => translate(`resources.kiosk-groups.${key}`, options);
  const base = `${KIOSK_GROUPS_BASE}/${record.id}`;

  useEffect(() => {
    setName(record.name);
    setDescription(record.description === null ? '' : record.description);
  }, [record.name, record.description]);

  const act = async (run: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    try {
      await run();
      notify(g('saved'), { type: 'success' });
      return true;
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
      return false;
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const detailsChanged = name.trim() !== record.name || textOf(description) !== record.description;
  const saveDetails = () => act(() => apiClient.patch(base, { name: name.trim(), description: textOf(description) }));
  const openChooser = () => {
    setChosen(record.members.map((member) => member.kioskId));
    setChoosing(true);
  };
  const saveMembers = async () => {
    if (await act(() => apiClient.put(`${base}/members`, { kioskIds: chosen }))) setChoosing(false);
  };
  const currentTestGroup = (allGroups.data || []).find((other: any) => other.isTestGroup === true && other.id !== record.id);
  const putTest = (value: boolean) => act(() => apiClient.put(`${base}/test-group`, { isTestGroup: value }));
  const setTest = (value: boolean) => {
    if ((value && currentTestGroup !== undefined) || (!value && record.isTestGroup)) {
      setConfirmTest(value);
      return;
    }
    putTest(value);
  };
  const confirmSetTest = async () => {
    if (confirmTest === null) return;
    await putTest(confirmTest);
    setConfirmTest(null);
  };
  const remove = async () => {
    setBusy(true);
    try {
      await apiClient.delete(base);
      notify(g('deleted'), { type: 'success' });
      redirect('list', 'kiosk-groups');
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, sm: 2 } }}>
      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {writer ? (
            <>
              <TextField label={g('fields.name')} value={name} onChange={(event) => setName(event.target.value)} inputProps={{ maxLength: 100, 'data-testid': 'group-name' }} />
              <TextField label={g('fields.description')} value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={2} inputProps={{ maxLength: 500, 'data-testid': 'group-description' }} />
              <Box>
                <Button variant="contained" disabled={busy || !detailsChanged || name.trim() === ''} onClick={saveDetails} sx={{ textTransform: 'none' }}>{g('actions.save')}</Button>
              </Box>
              <Box>
                <FormControlLabel control={<Switch checked={record.isTestGroup} disabled={busy} onChange={(event) => setTest(event.target.checked)} />} label={g('testGroup')} />
                <Typography variant="caption" color="text.secondary" component="div">{g('testGroupHelp')}</Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h6">{record.name}</Typography>
              {record.description !== null && <Typography variant="body2">{record.description}</Typography>}
              {record.isTestGroup && <Box><Chip size="small" color="warning" label={g('testGroup')} /></Box>}
            </>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6">{g('members')}</Typography>
            {writer && <Button variant="outlined" disabled={busy} onClick={openChooser} sx={{ textTransform: 'none' }}>{g('actions.chooseKiosks')}</Button>}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{g('membersNote')}</Typography>
          {record.members.length === 0 ? (
            <Typography variant="body2">{g('noMembers')}</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{g('fields.kiosk')}</TableCell>
                    <TableCell>{g('fields.site')}</TableCell>
                    <TableCell>{g('fields.status')}</TableCell>
                    <TableCell>{g('fields.addedAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.members.map((member) => (
                    <TableRow key={member.kioskId}>
                      <TableCell>{member.kioskName}</TableCell>
                      <TableCell>{member.siteName}</TableCell>
                      <TableCell>{translate(`resources.kiosk-devices.status.${member.status}`)}</TableCell>
                      <TableCell>{new Date(member.addedAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
      {writer && (
        <Box>
          <Button color="error" variant="outlined" disabled={busy} onClick={() => setConfirmDelete(true)} data-testid="group-delete" sx={{ textTransform: 'none' }}>{g('actions.delete')}</Button>
        </Box>
      )}
      <Dialog open={choosing} onClose={() => !busy && setChoosing(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{g('actions.chooseKiosks')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="group-kiosks">{g('fields.kiosk')}</InputLabel>
            <Select
              labelId="group-kiosks"
              label={g('fields.kiosk')}
              multiple
              value={chosen}
              onChange={(event) => setChosen(typeof event.target.value === 'string' ? event.target.value.split(',') : (event.target.value as string[]))}
            >
              {(kiosks.data || []).map((kiosk: any) => <MenuItem key={kiosk.id} value={kiosk.id}>{`${kiosk.name} (${kiosk.siteName})`}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChoosing(false)} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={saveMembers} disabled={busy} sx={{ textTransform: 'none' }}>{g('actions.save')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmTest !== null} onClose={() => !busy && setConfirmTest(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{g('testGroup')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmTest === false ? g('removeTestGroupConfirm') : g('replaceTestGroupConfirm', { group: record.name, current: currentTestGroup === undefined ? '' : currentTestGroup.name })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmTest(null)} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={confirmSetTest} disabled={busy} sx={{ textTransform: 'none' }}>{g('actions.save')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDelete} onClose={() => !busy && setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{g('actions.delete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{g('deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(false)} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy} sx={{ textTransform: 'none' }}>{g('actions.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const RecordDetails = () => {
  const record = useRecordContext<KioskGroupDetail>();
  if (!record) return null;
  return <KioskGroupDetails record={record} />;
};

export const KioskGroupShow = () => {
  const translate = useTranslate();
  return (
    <Show title={translate('resources.kiosk-groups.name')} actions={false}>
      <RecordDetails />
    </Show>
  );
};
